import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutGrid,
  LayoutDashboard,
  Download,
  FileText,
  Upload,
  Clock,
  Settings,
  ChevronRight,
  ChevronsLeft,
  Shield,
  Mail,
  // Building2 — used for the "Banking" + "Organization" rows; the rounded-square
  // double-pillar variant. Building — used only for the role-gated
  // "Organization" admin row in settings; the simpler single-pillar variant.
  // Two different glyphs in the lucide-react set, intentionally kept distinct.
  Building2,
  Building,
  User,
  Users,
  Store,
  FileInput,
  Tags,
  BarChart3,
  Plug,
  Wallet,
  Boxes,
  Landmark,
  CreditCard,
} from 'lucide-react'
import { cn, Button, Tooltip, TooltipTrigger, TooltipContent } from '@atrvd/saas-ui'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@atrvd/saas-ui'
import { useProfile } from '@/hooks/useProfile'
import { canAccessAdminPanel, canInviteUsers, canViewOrganizationSettings, hasPermission } from '@dcp/shared'
import { useSidebar } from '@/contexts/SidebarContext'

/**
 * Navigation item definition — a single link with label, route, and icon.
 */
interface NavItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

/**
 * Props for the reusable NavSection component that renders one collapsible
 * sidebar group in expanded mode or a tooltip-wrapped icon button in collapsed mode.
 */
interface NavSectionProps {
  /** Display name used as the section heading and tooltip label */
  label: string
  /** Lucide icon component rendered as the section trigger icon */
  icon: React.ComponentType<{ className?: string }>
  /** Child nav links for this section */
  items: NavItem[]
  /** Whether the Collapsible is currently open (expanded mode only) */
  open: boolean
  /** Callback to update the open state (expanded mode only) */
  onOpenChange: (open: boolean) => void
  /** Whether any child route in this section is currently active */
  isActive: boolean
  /** Whether the sidebar is in collapsed (icon-only) mode */
  isCollapsed: boolean
  /** Unique id passed to CollapsibleContent for aria-controls (expanded mode) */
  navId: string
}

/**
 * Banking nested items (collapsible)
 */
const bankingNavItems: NavItem[] = [
  // DCP-341: the page is called "Daily Banking" now. Only the LABEL changed —
  // the href stays `/banking/dashboard`, which is what the active-state match
  // (see `isBankingRoute` below), every redirect target and existing bookmarks
  // depend on.
  { label: 'Daily Banking', href: '/banking/dashboard', icon: LayoutDashboard },
  { label: 'Transactions Import', href: '/banking/transactions-import', icon: Download },
  { label: 'Balance Import', href: '/banking/balance-import', icon: Wallet },
  // Order mirrors the prototype: Line of Credit Availability sits ABOVE
  // Outstanding Checks (after Balance Import).
  //
  // Force a stable two-line label ("Availability" always on the second line).
  // The explicit "\n" is rendered via whitespace-pre-line (see NavSection), so
  // the wrap never toggles between one and two lines with the active-state font
  // weight or the exact sidebar width. Mirrors the prototype layout.
  { label: 'Line of Credit\nAvailability', href: '/banking/loc-availability', icon: Landmark },
  // DCP-374: directly below Line of Credit Availability, as in the prototype.
  // Same forced two-line label, for the same reason as its neighbour above.
  { label: 'Credit Card\nAvailability', href: '/banking/credit-card-availability', icon: CreditCard },
  { label: 'Outstanding Checks', href: '/banking/outstanding-checks', icon: FileText },
]

/**
 * Cash Position Summary link — lives in the Projections section (DCP-143).
 * Gated separately on `view_cash_position` (which EVERY product role has,
 * unlike `view_projections`), so it is shown even to roles that can't see the
 * projection input/import items (e.g. accounts_payable).
 */
const cashPositionNavItem: NavItem = {
  label: 'Cash Position Summary',
  href: '/projections/cash-position',
  icon: BarChart3,
}

/**
 * Projection input/import items — gated on `view_projections`.
 */
const projectionInputNavItems: NavItem[] = [
  { label: 'Projection Input', href: '/projections/input', icon: FileInput },
  { label: 'Projections Import', href: '/projections/projections-import', icon: Upload },
]

/**
 * Full ordered set of Projections-section routes, used only for active-route
 * detection (which section to open). The displayed subset is permission-built
 * inside the component.
 */
const projectionsNavItems: NavItem[] = [cashPositionNavItem, ...projectionInputNavItems]

/**
 * Admin nested items (collapsible) — visible only to admins
 */
const adminNavItems: NavItem[] = [
  { label: 'Invitations', href: '/admin/invitations', icon: Mail },
  { label: 'Organizations', href: '/admin/organizations', icon: Building2 },
]

/**
 * Renders one navigation section (e.g. Banking, Projections, Settings, Admin).
 *
 * In collapsed mode: renders a single icon button centered in the sidebar,
 * wrapped in a right-side Tooltip showing the section label. Clicking navigates
 * to the first route in the section.
 *
 * In expanded mode: renders a Collapsible group with a trigger row showing the
 * icon + label + chevron, and a CollapsibleContent with nested NavLinks.
 */
function NavSection({
  label,
  icon: Icon,
  items,
  open,
  onOpenChange,
  isActive,
  isCollapsed,
  navId,
}: NavSectionProps) {
  const navigate = useNavigate()

  // Collapsed mode — icon-only with tooltip and click-to-navigate behavior
  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => navigate(items[0].href)}
            className={cn(
              'flex items-center justify-center w-10 h-10 mx-auto rounded-md transition-colors',
              isActive
                ? 'text-blue-700 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            )}
            aria-label={label}
          >
            <Icon className="h-5 w-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    )
  }

  // Expanded mode — full collapsible section with labels and nested links
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger
        className={cn(
          'flex w-full items-center justify-between px-6 py-3 text-sm font-medium transition-colors',
          isActive ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
        )}
        aria-expanded={open}
        aria-controls={navId}
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5" aria-hidden="true" />
          <span>{label}</span>
        </div>
        <ChevronRight
          className={cn(
            'h-4 w-4 transition-transform duration-200',
            open && 'rotate-90'
          )}
          aria-hidden="true"
        />
      </CollapsibleTrigger>

      <CollapsibleContent id={navId}>
        <nav
          className="mt-1 flex flex-col gap-0.5 ml-9 border-l border-gray-200"
          aria-label={`${label} pages`}
        >
          {items.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive: linkActive }) =>
                cn(
                  'flex items-center gap-3 py-2 pl-4 pr-4 text-sm transition-colors',
                  linkActive
                    ? 'text-blue-700 border-l-2 border-l-blue-600 -ml-[1px] font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                )
              }
            >
              {/* shrink-0 keeps every item's icon fully visible and unsquished
                  even when a label wraps to two lines (e.g. Line of Credit
                  Availability). */}
              <item.icon className="h-4 w-4 shrink-0" />
              {/* whitespace-pre-line honors an explicit "\n" in the label for a
                  stable multi-line wrap; min-w-0 lets the text wrap inside the
                  flex row instead of overflowing; leading-tight keeps the two
                  lines compact. */}
              <span className="min-w-0 whitespace-pre-line leading-tight">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Sidebar navigation with collapsible sections and support for collapsed
 * (icon-only) and expanded modes driven by SidebarContext.
 *
 * In collapsed mode the sidebar is 64 px wide and shows only section icons
 * with right-side tooltips. In expanded mode it is 256 px wide with full
 * labels and collapsible sub-navigation.
 *
 * Admin section is role-gated and only visible to super_admin / admin roles.
 */
export function SidebarNav() {
  const location = useLocation()
  const { data: profile } = useProfile()
  const { isCollapsed, toggle } = useSidebar()
  const canViewBanking = hasPermission(profile?.productRole, 'view_banking')
  const canViewProjections = hasPermission(profile?.productRole, 'view_projections')
  const canViewCashPosition = hasPermission(profile?.productRole, 'view_cash_position')
  const canManageSettings = hasPermission(profile?.productRole, 'manage_settings')

  // Projections-section links, permission-built: Cash Position Summary shows for
  // anyone with view_cash_position; the input/import items require
  // view_projections. Roles like accounts_payable (cash-position but no
  // projections) therefore still get the Cash Position link.
  const projectionsSectionItems: NavItem[] = [
    ...(canViewCashPosition ? [cashPositionNavItem] : []),
    ...(canViewProjections ? projectionInputNavItems : []),
  ]

  // Build settings nav items based on role.
  const canManageTeam =
    !!profile?.role &&
    canInviteUsers({ role: profile.role, product_role: profile.productRole })

  // canViewOrgSettings admits super_admin + org_owner (can edit name) and
  // product admin / system admin (see the page with the name field disabled).
  // org_owner has productRole=null so canManageSettings alone would hide the
  // link — use the broader helper here instead. DCP-158.
  const canViewOrgSettings =
    !!profile?.role && canViewOrganizationSettings({ role: profile.role, productRole: profile.productRole })

  const settingsNavItems: NavItem[] = [
    { label: 'Profile', href: '/settings/profile', icon: User },
    ...(canManageSettings
      ? [
          { label: 'Facilities', href: '/settings/facilities', icon: Store },
          { label: 'Packs', href: '/settings/packs', icon: Boxes },
          { label: 'Financial Accounts', href: '/settings/financial-accounts', icon: Building2 },
          { label: 'Categories', href: '/settings/categories', icon: Tags },
        ]
      : []),
    ...(canManageTeam ? [{ label: 'Team', href: '/settings/users', icon: Users }] : []),
    ...(canViewOrgSettings
      ? [{ label: 'Organization', href: '/settings/organization', icon: Building }]
      : []),
    ...(canManageSettings
      ? [{ label: 'Sage Intacct', href: '/settings/sage', icon: Plug }]
      : []),
  ]

  // Determine which section is currently active based on the current pathname.
  // Cash Position Summary now lives in the Projections section (its route is
  // included in projectionsNavItems), so it opens Projections, not Banking.
  const isBankingRoute = bankingNavItems.some((item) => location.pathname === item.href)
  const isAdminRoute = adminNavItems.some((item) => location.pathname.startsWith(item.href))
  const isSettingsRoute = settingsNavItems.some((item) =>
    location.pathname.startsWith(item.href)
  )
  const isProjectionsRoute = projectionsNavItems.some(
    (item) => location.pathname.startsWith(item.href)
  )

  // Whether the current user can access the Admin section
  const canAccessAdmin = profile?.role && canAccessAdminPanel(profile.role)

  // Track each section's open/closed state independently
  const [bankingOpen, setBankingOpen] = useState(isBankingRoute)
  const [adminOpen, setAdminOpen] = useState(isAdminRoute)
  const [settingsOpen, setSettingsOpen] = useState(isSettingsRoute)
  const [projectionsOpen, setProjectionsOpen] = useState(isProjectionsRoute)

  return (
    <aside
      className={cn(
        'fixed top-16 bottom-0 left-0 z-30 flex flex-col border-r border-gray-200 bg-white transition-[width] duration-300',
        isCollapsed ? 'w-16 overflow-hidden' : 'w-64 overflow-y-auto'
      )}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Nav sections — grows to fill available space */}
      <div
        className={cn(
          'flex flex-1 flex-col py-4',
          isCollapsed && 'space-y-2'
        )}
      >
        {/* Banking Section — gated on view_banking */}
        {canViewBanking && (
          <NavSection
            label="Banking"
            icon={LayoutGrid}
            items={bankingNavItems}
            open={bankingOpen}
            onOpenChange={setBankingOpen}
            isActive={isBankingRoute}
            isCollapsed={isCollapsed}
            navId="banking-nav"
          />
        )}

        {/* Projections Section — visible to anyone with view_projections OR
            view_cash_position (the Cash Position Summary link lives here and is
            broader than the projection input/import items). */}
        {projectionsSectionItems.length > 0 && (
          <NavSection
            label="Projections"
            icon={Clock}
            items={projectionsSectionItems}
            open={projectionsOpen}
            onOpenChange={setProjectionsOpen}
            isActive={isProjectionsRoute}
            isCollapsed={isCollapsed}
            navId="projections-nav"
          />
        )}

        {/* Settings Section */}
        <NavSection
          label="Settings"
          icon={Settings}
          items={settingsNavItems}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          isActive={isSettingsRoute}
          isCollapsed={isCollapsed}
          navId="settings-nav"
        />

        {/* Admin Section — role-gated, separated by a top border */}
        {canAccessAdmin && (
          <div
            className={cn(
              'border-t border-gray-200',
              isCollapsed ? 'mt-2 pt-2' : 'mt-4 pt-4'
            )}
          >
            <NavSection
              label="Admin"
              icon={Shield}
              items={adminNavItems}
              open={adminOpen}
              onOpenChange={setAdminOpen}
              isActive={isAdminRoute}
              isCollapsed={isCollapsed}
              navId="admin-nav"
            />
          </div>
        )}
      </div>

      {/* Collapse toggle — pinned at bottom */}
      <div className="border-t border-gray-200 p-2">
        <Button
          variant="ghost"
          size={isCollapsed ? 'icon' : 'default'}
          onClick={toggle}
          className={cn(
            'w-full text-gray-500 hover:text-gray-900',
            !isCollapsed && 'justify-start gap-2 px-4'
          )}
          aria-label="Toggle sidebar"
        >
          <ChevronsLeft
            className={cn(
              'h-4 w-4 transition-transform duration-300',
              isCollapsed && 'rotate-180'
            )}
          />
          {!isCollapsed && <span className="text-sm">Collapse</span>}
        </Button>
      </div>
    </aside>
  )
}
